// Debug-only visualizer switcher + FPS overlay - not part of the
// pilot's real on-screen UI (same "hidden unless toggled" precedent as
// the palette-debug panel). Lets each visualizer be isolated and
// iterated on by button instead of hand-editing sources and rebuilding
// for every switch. Also the left/right-arrow visualizer cycling the
// original brief called for - not a separate mechanism. Debug toggle
// moved off Up/Down (now the TV remote's own volume control, see
// _onKeyDown) onto 'd' - a real remote has no such key, so this stays a
// keyboard-only dev convenience without risking an accidental trigger
// from the couch. The rig head-solo switcher this used to also drive
// was removed along with its own on-screen menu - dev-only clutter on
// the real simulator screen, not something worth keeping around.

// The debug panel is laid out by PROFILE.danceability, descending, across
// THREE columns - most danceable first, left to right - and each button
// carries its own score. Three rather than two since the library passed
// fifty entries: at two columns each ran twenty-six deep and the bottom of
// the second fell off the frame on a laptop, taking the calmest visualizers
// with it - exactly the ones a debug panel exists to reach. It used to be three columns keyed to
// entry.group (PARTY / NEUTRES / CLASSIQUES); that grouping is still computed
// and still attached to every entry by main.js's switcherGroup, it just no
// longer decides the layout. Left there rather than deleted along with the
// columns: it carries real editorial judgement that no other field does (seven
// entries state a group that contradicts what their energyLevel would imply),
// and throwing that away would be a side effect of a layout change rather than
// a decision about it.
function urlParam(name) {
  if (typeof location === "undefined") return null;
  return new URLSearchParams(location.search).get(name);
}

const SWITCHER_COLUMNS = 3;
const TYPED_IDLE_MS = 4000;
// Touch swipe (options.swipe): minimum horizontal travel, maximum duration,
// and how much more horizontal than vertical the gesture has to be, so a
// scroll-ish or diagonal drag is not mistaken for one. A plain tap moves
// nowhere and never qualifies, so taps (the cover's pause) are untouched.
const SWIPE_MIN_PX = 60;
const SWIPE_MAX_MS = 800;
const SWIPE_AXIS_RATIO = 1.5;

// Entries with no danceability at all - AdaptiveParty and NeonPulse carry no
// PROFILE, by the registry's own deliberate exclusions - sort to the END
// rather than to zero. Sorting them as 0 would bury the orchestrated mode
// among the stillest pieces as though that were a measurement, when in fact
// nothing was measured.
function danceRank(entry) {
  return entry && entry.danceability != null ? entry.danceability : -1;
}

function danceLabel(entry) {
  return entry && entry.danceability != null ? entry.danceability.toFixed(2) : '\u2014';
}

export class PartyModeController {
  // entries: [{ name, group, create }] - create() returns a fresh Visualizer
  // instance (SceneManager.setVisualizer always wants a new one, not a
  // reused instance, since dispose() runs on the outgoing one).
  constructor(sceneManager, entries, options) {
    this._sceneManager = sceneManager;
    this._entries = entries;
    // Which variants the switcher shows. Most visualizers appear several times
    // - Ocean T2 / Ocean T1, Portal T2 / Portal T0 - and listing every one of
    // them made the panel long and buried the entry you actually want. Default
    // is one button per visualization, the best-quality variant; 'degraded'
    // swaps to the lower-tier variants alone, for comparing tiers side by side.
    this._variantMode = 'performance';
    this._poolIds = null;
    this._panel = options.panelEl;
    // Le nombre de colonnes du panneau (3 par défaut). La diffusion de la
    // console en demande UNE, qui défile : à cent dix visualiseurs, trois
    // colonnes couvraient la moitié de l'écran (13/10/2026).
    this._colonnes = Math.max(1, Number(options.columns) || SWITCHER_COLUMNS);
    this._fpsEl = options.fpsEl;
    this._scheduleDebugEl = options.scheduleDebugEl || null;
    // Le nom de la visualisation, en bas a droite. Toujours alimente - pas
    // derriere _debugActive comme le compteur de fps, parce que la touche 'd'
    // qui l'ouvre n'existe pas sur une telecommande et que c'est justement sur
    // la television qu'on a besoin de savoir ce qui tourne. ?viz=0 le retire.
    this._vizNameEl = options.vizNameEl || null;
    if (this._vizNameEl && urlParam('viz') === '0') {
      this._vizNameEl.style.display = 'none';
      this._vizNameEl = null;
    }
    this._vizNameLast = null;
    // Called with 'previous'/'next' on a Left/Right press while the debug
    // panel is hidden - see _onKeyDown. Optional so this controller still
    // works standalone without a caller wiring up Sonos transport control.
    this._onTrackSkip = options.onTrackSkip || null;
    // Called with +1/-1 on an Up/Down press - see _onKeyDown. Same
    // optional-callback shape as onTrackSkip above.
    this._onVolumeChange = options.onVolumeChange || null;
    // Called on 'L'/center-OK instead of toggling body.lyrics-active
    // directly - lets the caller check whether there's actually anything
    // to show first (see main.js's handleLyricsToggle) and display
    // "Paroles indisponibles" instead when there isn't. Falls back to a
    // plain toggle when absent, so this controller still works standalone.
    this._onLyricsToggle = options.onLyricsToggle || null;
    // Optional (the /radio page): Left/Right while the debug panel is hidden
    // call this with +1/-1 INSTEAD of onTrackSkip - the remote's arrows move
    // between visuals rather than between tracks there. Only one of the two
    // is ever wired on a given page.
    this._onVisualStep = options.onVisualStep || null;
    // Optional (the /radio page): Space, or a remote's play/pause key, calls
    // this - the listener's own pause. Checked before the typed-sequence
    // buffer so a space is never swallowed as "typing".
    this._onPlayPause = options.onPlayPause || null;
    // Optional: when set, the 'd' key alone no longer opens the debug panels.
    // The word has to be typed and followed by Enter - a guard for a page
    // that is public (the /radio page), where a stray key on a keyboard must
    // not reveal the technical readouts. Letters typed accumulate in
    // _typed, cleared after TYPED_IDLE_MS of silence.
    this._debugSequence = options.debugSequence || null;
    this._typed = '';
    this._typedAt = 0;
    // Optional single-letter shortcuts { b: fn, ... } (the /radio page's
    // external stations). In debugSequence mode they are consulted from the
    // typed buffer so that typing the word itself ("de-b-ug") never fires
    // one - only a letter that is not continuing the sequence does.
    this._hotkeys = options.hotkeys || null;
    this._volumeIndicatorEl = options.volumeIndicatorEl || null;
    this._volumeIndicatorIconEl = options.volumeIndicatorIconEl || null;
    this._volumeIndicatorHideTimer = null;
    this._debugActive = false;

    this._frameCount = 0;
    this._fpsLastSample = performance.now();

    this._buildPanel();
    window.addEventListener('keydown', (e) => this._onKeyDown(e));
    // Mobile: a horizontal swipe is the remote's Left/Right, same direction
    // as the key it stands for - swipe left = ArrowLeft, swipe right =
    // ArrowRight.
    if (options.swipe) this._bindSwipe();

    const startIndex = options.startIndex || 0;
    this._select(startIndex);

    this._tick = this._tick.bind(this);
    requestAnimationFrame(this._tick);
  }

  // Groups the entries by the visualization they belong to and marks which
  // one of each group is the top-quality variant. The tier lives in the entry
  // NAME ('Ocean T2'), which is the only place it is written down - the create
  // callback closes over it and nothing else exposes it.
  _variantInfo() {
    if (this._variants) return this._variants;
    const TIER = /^(.*?)\s+T(\d)$/;
    const groups = new Map();
    const info = this._entries.map((entry, i) => {
      const m = TIER.exec(entry.name);
      const base = m ? m[1] : entry.name;
      if (!groups.has(base)) groups.set(base, []);
      groups.get(base).push(i);
      return { base: base, tier: m ? parseInt(m[2], 10) : null, top: true, alone: true };
    });
    groups.forEach((idx) => {
      let bestI = idx[0];
      for (const i of idx) {
        const t = info[i].tier, bt = info[bestI].tier;
        if (t != null && (bt == null || t > bt)) bestI = i;
      }
      for (const i of idx) { info[i].top = (i === bestI); info[i].alone = (idx.length === 1); }
    });
    this._variants = info;
    return info;
  }

  // An entry belongs in the panel when it matches the current filter. In
  // 'degraded' a visualization with no second variant has nothing to show, so
  // it drops out entirely - that is the point of the mode.
  _isVisible(i) {
    const v = this._variantInfo()[i];
    return this._variantMode === 'degraded' ? !v.top : v.top;
  }

  _buildPanel() {
    this._panel.innerHTML = '';
    // Index-aligned with _entries, but SPARSE now that the panel filters: an
    // entry the current mode hides simply has no button. Array.forEach skips
    // unassigned slots, and _step already falls back to the first rendered
    // entry when the selected one is not on screen, so both survive the holes.
    this._buttons = new Array(this._entries.length);
    // The order Left/Right walks. Built from the RENDERED layout rather than
    // from _entries' own order, so arrowing through the panel follows what is
    // on screen instead of jumping between columns.
    this._order = [];

    // Ties broken by the entry's own position, so the order is stable: the
    // four visualizers that appear twice as T2/T0 variants share a score, and
    // without this they could swap places between builds.
    const modes = document.createElement('div');
    modes.className = 'switcher-modes';
    [['performance', 'performance'], ['degraded', 'degrades']].forEach(([key, label]) => {
      const b = document.createElement('button');
      b.textContent = label;
      if (this._variantMode === key) b.classList.add('active');
      b.addEventListener('click', () => {
        if (this._variantMode === key) return;
        this._variantMode = key;
        this._buildPanel();
        // Rebuilding threw the highlights away; put them back WITHOUT going
        // through _select, which would restart the running visualizer.
        this._markActive();
        this.setPoolIds(this._poolIds);
      });
      modes.appendChild(b);
    });
    this._panel.appendChild(modes);

    const body = document.createElement('div');
    body.className = 'switcher-columns';
    this._panel.appendChild(body);

    const ranked = this._entries.map((entry, i) => i)
      .filter((i) => this._isVisible(i))
      .sort((a, b) => {
        const d = danceRank(this._entries[b]) - danceRank(this._entries[a]);
        return d !== 0 ? d : a - b;
      });
    if (ranked.length === 0) return;

    const perColumn = Math.ceil(ranked.length / this._colonnes);
    for (let c = 0; c < this._colonnes; c++) {
      const slice = ranked.slice(c * perColumn, (c + 1) * perColumn);
      if (slice.length === 0) continue;
      const column = document.createElement('div');
      column.className = 'switcher-column';
      const heading = document.createElement('div');
      heading.className = 'switcher-heading';
      // The range this column actually covers, read off its own contents
      // rather than hard-coded - so it stays true when a visualizer is added
      // or a score is retuned.
      heading.textContent = danceLabel(this._entries[slice[0]])
        + ' \u2192 ' + danceLabel(this._entries[slice[slice.length - 1]])
        + ' \u00b7 ' + slice.length;
      column.appendChild(heading);
      for (const i of slice) {
        const btn = document.createElement('button');
        // In 'performance' the tier suffix is noise - there is exactly one
        // button per visualization, so the name alone says everything.
        const v = this._variantInfo()[i];
        const label = this._variantMode === 'performance' ? v.base : this._entries[i].name;
        btn.textContent = label + '  ' + danceLabel(this._entries[i]);
        btn.addEventListener('click', () => this._select(i));
        column.appendChild(btn);
        this._buttons[i] = btn;
        this._order.push(i);
      }
      body.appendChild(column);
    }
  }

  // The danceability pool for the track playing now - the visualizers the
  // orchestrator is actually allowed to choose between (see orchestrator.js's
  // selectDanceabilityPool). Marked rather than filtered: every button stays
  // clickable, because the whole point of this panel is being able to force a
  // visualizer the orchestrator would not have picked.
  setPoolIds(ids) {
    this._poolIds = ids || null;
    const pool = ids && ids.length ? new Set(ids) : null;
    if (!this._buttons) return;
    for (let i = 0; i < this._entries.length; i++) {
      const btn = this._buttons[i];
      if (!btn) continue;
      const id = this._entries[i].id;
      btn.classList.toggle('in-pool', !!(pool && id && pool.has(id)));
    }
  }

  // Left/Right, in displayed order. Falls back to plain index stepping if a
  // caller ever hands over entries this panel did not render.
  _step(delta) {
    if (!this._order || this._order.length === 0) {
      this._select(this._index + delta);
      return;
    }
    const at = this._order.indexOf(this._index);
    if (at === -1) {
      this._select(this._order[0]);
      return;
    }
    const n = this._order.length;
    this._select(this._order[((at + delta) % n + n) % n]);
  }

  _select(index) {
    this._index = ((index % this._entries.length) + this._entries.length) % this._entries.length;
    const entry = this._entries[this._index];
    this._sceneManager.setVisualizer(entry.create());
    this._markActive();
  }

  // Just the highlight. Switching the panel's filter must not restart what is
  // already playing.
  _markActive() {
    if (!this._buttons) return;
    this._buttons.forEach((btn, i) => btn.classList.toggle('active', i === this._index));
    // En colonne défilante, le visualiseur actif reste visible (flèches,
    // balayage, choix de l'orchestration).
    const actif = this._buttons[this._index];
    if (actif && this._colonnes === 1 && actif.scrollIntoView) actif.scrollIntoView({ block: 'nearest' });
  }

  _bindSwipe() {
    let start = null;
    window.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { start = null; return; }
      const t = e.touches[0];
      start = { x: t.clientX, y: t.clientY, at: performance.now() };
    }, { passive: true });
    window.addEventListener('touchend', (e) => {
      if (!start || e.changedTouches.length !== 1) { start = null; return; }
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const dt = performance.now() - start.at;
      start = null;
      if (dt > SWIPE_MAX_MS || Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_AXIS_RATIO) return;
      this._horizontalStep(dx < 0 ? -1 : 1);
    }, { passive: true });
    window.addEventListener('touchcancel', () => { start = null; }, { passive: true });
  }

  // What Left/Right (and a swipe) do: the switcher while the debug panel is
  // open, otherwise the visual step or the track skip, whichever is wired.
  _horizontalStep(direction) {
    if (this._debugActive) this._step(direction);
    else if (this._onVisualStep) this._onVisualStep(direction);
    else if (this._onTrackSkip) this._onTrackSkip(direction > 0 ? 'next' : 'previous');
  }

  // Left/Right is the visualizer switcher while the debug panel is open
  // (a dev convenience, unchanged), but the remote's actual Left/Right
  // buttons during normal viewing (debug hidden - the default/production
  // state) skip to the previous/next track instead - see onTrackSkip.
  // Up/Down is always volume, debug panel open or not - "le bouton haut
  // et bas de la telecommande de la tv regle le volume" - unlike Left/
  // Right there's no dev-mode alternate meaning to preserve for it.
  _onKeyDown(e) {
    // Quelqu'un est en train d'écrire (le champ de recherche de la jam) : ses
    // lettres lui appartiennent. Sans ce garde-fou, taper « stromae » change
    // de station au « r » et coupe la radio personnelle.
    const el = e.target;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
    if (this._onPlayPause && (e.key === ' ' || e.key === 'MediaPlayPause')) {
      e.preventDefault();
      this._onPlayPause();
      return;
    }
    if (this._debugSequence && this._trackTypedSequence(e)) return;
    if (e.key === 'ArrowRight') this._horizontalStep(1);
    else if (e.key === 'ArrowLeft') this._horizontalStep(-1);
    else if (e.key === 'ArrowUp') this._changeVolume(1);
    else if (e.key === 'ArrowDown') this._changeVolume(-1);
    else if (e.key === 'd' && !this._debugSequence) this._toggleDebug();
    else if (e.key === 'l' || e.key === 'L') this._toggleLyricsBanner();
    // Remote's center/OK button - fires as a standard 'Enter' keydown
    // (no tizenhwkey special-casing needed, that's for 'back' only - see
    // party-mode.html's own Tizen script). Same action as 'L' above, and
    // as tv.js's own Enter handler in classic mode - one physical button,
    // same toggle, both modes.
    else if (e.key === 'Enter') this._toggleLyricsBanner();
  }

  // debugSequence mode only. Single printable keys accumulate; Enter with the
  // sequence as the tail of what was typed toggles the panels and is
  // swallowed (it must not also toggle the lyrics banner). Returns true when
  // the event has been consumed here. Everything else - arrows, a plain
  // Enter with nothing typed - falls through untouched.
  _trackTypedSequence(e) {
    const now = performance.now();
    if (now - this._typedAt > TYPED_IDLE_MS) this._typed = '';
    this._typedAt = now;
    if (e.key === 'Enter') {
      const hit = this._typed.length > 0 && this._typed.slice(-this._debugSequence.length) === this._debugSequence;
      this._typed = '';
      if (hit) {
        this._toggleDebug();
        return true;
      }
      return false;
    }
    if (e.key.length === 1) {
      this._typed = (this._typed + e.key.toLowerCase()).slice(-32);
      const k = e.key.toLowerCase();
      if (this._hotkeys && this._hotkeys[k] && !this._typedIsSequencePrefix()) {
        this._typed = '';
        this._hotkeys[k]();
        return true;
      }
      // 'l' is a real shortcut (lyrics) on this page too - let it through so
      // typing the word does not steal it, but no other single key acts.
      return e.key !== 'l' && e.key !== 'L';
    }
    return false;
  }

  // True while the tail of what was typed is the beginning of the debug
  // word ("d", "de", "deb"...): a hotkey letter inside it is part of the
  // word, not a command.
  _typedIsSequencePrefix() {
    const seq = this._debugSequence;
    for (let n = Math.min(this._typed.length, seq.length); n >= 1; n--) {
      if (this._typed.slice(-n) === seq.slice(0, n)) return true;
    }
    return false;
  }

  // direction: 1 (up) or -1 (down). Fires the callback (Sonos volume
  // change) and flashes the on-screen arrow regardless of whether a
  // callback was actually wired up, so the indicator still works when
  // this controller is used standalone.
  _changeVolume(direction) {
    if (this._onVolumeChange) this._onVolumeChange(direction);
    this._showVolumeIndicator(direction);
  }

  // "un symbole [...] qui apparait uniquement au moment ou les boutons
  // sont presses" - flashes in, then fades back out on its own after a
  // beat. Restarting the hide timer on every press (rather than letting
  // an earlier one fire mid-repeat) is what keeps it visibly solid
  // through a held/repeated key instead of flickering.
  _showVolumeIndicator(direction) {
    if (!this._volumeIndicatorEl) return;
    if (this._volumeIndicatorIconEl) {
      this._volumeIndicatorIconEl.textContent = direction > 0 ? '▲' : '▼';
    }
    this._volumeIndicatorEl.classList.add('visible');
    clearTimeout(this._volumeIndicatorHideTimer);
    this._volumeIndicatorHideTimer = setTimeout(() => {
      this._volumeIndicatorEl.classList.remove('visible');
    }, 900);
  }

  _toggleDebug() {
    this._debugActive = !this._debugActive;
    document.body.classList.toggle('debug-active', this._debugActive);
  }

  // 'L' - transparent 3-line synced-lyrics banner (see #lyrics-banner in
  // party-mode.html). Purely a CSS visibility toggle here; the actual
  // fetch/sync/render logic lives in main.js (same split as this
  // controller's own FPS overlay vs. scene-manager.js's real frame timing).
  _toggleLyricsBanner() {
    if (this._onLyricsToggle) {
      this._onLyricsToggle();
      return;
    }
    document.body.classList.toggle('lyrics-active');
  }

  // Runs its own rAF loop purely to sample frame cadence, rather than
  // hooking into SceneManager's render loop - keeps the FPS counter
  // fully decoupled from the already-verified render/update pipeline.
  _tick() {
    this._frameCount++;
    const now = performance.now();
    const elapsed = now - this._fpsLastSample;
    if (elapsed >= 500) {
      const fps = Math.round((this._frameCount * 1000) / elapsed);
      this._frameCount = 0;
      this._fpsLastSample = now;
      if (this._debugActive && this._fpsEl) {
        // Raw last-frame ms alongside the rounded fps (CHANTIER 7's own
        // governor reads this same value's rolling average to decide
        // quality tier - this is the instantaneous number, not that
        // average, so it can look noisier than the fps figure next to it).
        const frameMs = this._sceneManager.getFrameMs ? this._sceneManager.getFrameMs() : null;
        const tier = this._sceneManager.getQualityTier ? this._sceneManager.getQualityTier() : null;
        this._fpsEl.textContent = fps + ' fps · ' + (frameMs != null ? frameMs.toFixed(1) + 'ms' : '') +
          (tier != null ? ' · T' + tier : '') + ' · ' + this._entries[this._index].name;
      }
      this._updateScheduleDebug();
      this._updateVizName();
    }
    requestAnimationFrame(this._tick);
  }

  // CHANTIER 6 (revision) - mode courant, duree calculee (temps + secondes),
  // temps ecoule, swap arme ou non, pace. Only AdaptivePartyVisualizer
  // exposes getScheduleDebugInfo (duck-typed check) - every other
  // switcher entry leaves this panel empty rather than erroring.
  // Ce qui est REELLEMENT a l'ecran. Sous AdaptiveParty l'entree selectionnee
  // s'appelle "AdaptiveParty", ce qui ne dit rien : la composition que
  // l'orchestrateur a choisie est la seule reponse utile, et elle change sans
  // que la selection bouge. On la prend quand le visualiseur actif sait la
  // donner (getScheduleDebugInfo().key), sinon on retombe sur le nom de
  // l'entree - qui est alors exactement ce qui tourne.
  //
  // Ecrit seulement quand le texte change : c'est une ecriture DOM toutes les
  // 500 ms sinon, pour un libelle qui bouge une fois par minute.
  // DEUX PORTES POUR LA CONSOLE EMBARQUÉE. Elle affiche le nom du visualiseur
  // dans sa propre barre et ses flèches doivent faire ce que font Gauche et
  // Droite. Deux méthodes publiques plutôt qu'un accès au champ privé : le
  // contrôleur est partagé avec judicael.com, qui n'appelle ni l'une ni
  // l'autre et ne voit donc aucun changement.
  setVizNameEl(el) {
    this._vizNameEl = el || null;
    this._vizNameLast = null;
    this._updateVizName();
  }

  step(delta) { this._step(delta); }

  _updateVizName() {
    if (!this._vizNameEl) return;
    const av = this._sceneManager.activeVisualizer;
    let name = null;
    if (av && typeof av.getScheduleDebugInfo === 'function') {
      const info = av.getScheduleDebugInfo();
      if (info && info.key) name = info.key;
    }
    if (!name) {
      const entry = this._entries[this._index];
      name = entry ? entry.name : '—';
    }
    const tier = this._sceneManager.getQualityTier ? this._sceneManager.getQualityTier() : null;
    const text = name + (tier != null ? ' · T' + tier : '');
    if (text === this._vizNameLast) return;
    this._vizNameLast = text;
    this._vizNameEl.textContent = text;
  }

  _updateScheduleDebug() {
    if (!this._scheduleDebugEl) return;
    if (!this._debugActive) return;
    const av = this._sceneManager.activeVisualizer;
    if (!av || typeof av.getScheduleDebugInfo !== 'function') {
      this._scheduleDebugEl.textContent = '';
      return;
    }
    const info = av.getScheduleDebugInfo();
    const fmt = (n, digits) => (n == null ? '—' : n.toFixed(digits));
    this._scheduleDebugEl.textContent =
      'mode ' + (info.mode || '—') + '\n' +
      'duree ' + fmt(info.desiredBeats, 1) + ' temps / ' + fmt(info.desiredSeconds, 2) + 's\n' +
      'ecoule ' + fmt(info.elapsedSeconds, 2) + 's\n' +
      'arme ' + (info.armed ? (info.armedType || 'oui') : 'non') + '\n' +
      'pace ' + fmt(info.pace, 2);
  }
}
